import React, { useEffect, useCallback, useState } from "react";
import {
  Slideout,
  TextField,
  Typography,
  Button,
  Icon,
  TextDescription,
} from "@ann/web-components";
import {
  SlideoutContainer,
  SlideoutHeader,
  SlideoutContent,
  BackButton,
  HeaderContent,
  TitleContainer,
  SaveButtonContainer,
  StyledForm,
} from "./ToolConfigurationSlideout.styled";

import Form from "@rjsf/core";
import validator from "@rjsf/validator-ajv8";

const INITIAL_FORM = {
  schedulingLink: "",
  orderName: "",
  description: "",
};

const schema = {
  title: "Tool Configuration",
  type: "object",
  properties: {
    schedulingLink: {
      type: "string",
      title: "Repair Scheduling Link",
    },
    orderName: {
      type: "string",
      title: "Order Name",
    },
    test: {
      type: "string",
      title: "Description (Optional)",
    },
  },
  required: ["schedulingLink", "orderName"],
};

const widgets = {
  TextWidget: TextField,
  TextAreaWidget: TextDescription,
};

const uiSchema = {
  "ui:title": false,
  "ui:classNames": "custom-form",
  "ui:submitButtonOptions": {
    norender: true,
  },
  "ui:field": {
    "ui:widget": "TextWidget",
    "ui:classNames": "form-field",
  },
};

Object.keys(schema.properties).forEach((key) => {
  uiSchema[key] = {
    "ui:options": {
      label: false,
    },
  };
});

const ToolConfigurationSlideout = ({
  open,
  onClose,
  tool,
  onSave,
  showBackButton = false,
  onBack,
}) => {
  const [formData, setFormData] = useState(INITIAL_FORM);

  useEffect(() => {
    if (open && tool) setFormData(INITIAL_FORM);
  }, [open, tool]);

  const handleSave = useCallback(() => {
    if (!onSave) return;
    onSave(formData);
  }, [formData, onSave]);

  const handleClose = useCallback(() => {
    setFormData(INITIAL_FORM);
    onClose?.();
  }, [onClose]);

  const handleBackKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter" || e.key === " ") onBack?.();
    },
    [onBack]
  );

  if (!tool) return null;

  return (
    <Slideout open={open} onClose={handleClose} size="medium">
      {showBackButton && (
        <Slideout.HeaderActions>
          <BackButton
            onClick={onBack}
            onKeyDown={handleBackKeyDown}
            role="button"
            tabIndex={0}
            aria-label="Go back"
          >
            <Icon name="arrowLeft" size="medium" />
          </BackButton>
        </Slideout.HeaderActions>
      )}

      <SlideoutContainer>
        <SlideoutHeader>
          <HeaderContent>
            <TitleContainer>
              <Typography category="title" size="large" weight="bold">
                {`Configure ${tool.title}`}
              </Typography>
            </TitleContainer>
          </HeaderContent>
        </SlideoutHeader>

        <SlideoutContent>
          <StyledForm>
            <Form
              schema={schema}
              validator={validator}
              widgets={widgets}
              uiSchema={uiSchema}
              formData={formData}
              onChange={({ formData: newFormData }) =>
                setFormData(newFormData)
              }
              onSubmit={({ formData: submitData }) => {
                if (onSave) {
                  onSave(submitData);
                  onClose?.();
                }
              }}
            />
          </StyledForm>
        </SlideoutContent>
        <SaveButtonContainer>
          <Button
            label="Save"
            size="medium"
            type="primary"
            onClick={handleSave}
          />
        </SaveButtonContainer>
      </SlideoutContainer>
    </Slideout>
  );
};

export default ToolConfigurationSlideout;

